import React, { useState, useEffect } from "react";
import { Tab, Tabs, Box, Typography, Paper, TextField } from "@mui/material";
import BookingCostsTab from "./BookingCostsTab";
import OperationsCostsTab from "./OperationsCostsTab";
import StaffTab from "../staff/StaffTab";
import CleaningSessionManager from "./CleaningSessionManager";
import PayStubGenerator from "./PayStubGenerator";
import SalariesTab from "./SalariesTab";
import CostsReportTab from "./CostsReportTab";
import OwnerMenu from "../owner/OwnerMenu";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next"; 

const OwnerCosts = ({ apartmentId }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [bookingCosts, setBookingCosts] = useState([]);
  const [operationsCosts, setOperationsCosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Payroll month/year state
  const now = new Date();
  const [payrollYear, setPayrollYear] = useState(now.getFullYear());
  const [payrollMonth, setPayrollMonth] = useState(now.getMonth() + 1);

   const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  /**
   * Fetch both booking and operational costs
   */
  const fetchCosts = async () => {
    setLoading(true);
    try {
      const [bookingRes, opsRes] = await Promise.all([
        fetch(`${getApiBaseUrl()}/api/costs/booking-costs/`),
        fetch(`${getApiBaseUrl()}/api/costs/operational-costs/`)
      ]);

      const bookingData = bookingRes.ok ? await bookingRes.json() : [];
      const opsData = opsRes.ok ? await opsRes.json() : [];

      setBookingCosts(bookingData);
      setOperationsCosts(opsData);
    } catch (error) {
      console.error("Error fetching costs:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initial data load
   */
  useEffect(() => {
    fetchCosts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartmentId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <NavBar />
        <OwnerMenu />
        <div className="ml-48 mt-16 p-6">
          <Typography>Loading costs...</Typography>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      <OwnerMenu />

      <div className="ml-48 mt-16 p-6">
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {t("cost_management")}
          </Typography>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ mb: 3 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t("booking_costs")} />
            <Tab label={t("operations_costs")} />
            <Tab label={t("staff")} />
            <Tab label="Cleaning Sessions" />
            <Tab label="Pay Stubs" />
            <Tab label="Salaries" />
            <Tab label="Costs Report" />
          </Tabs>

          {/* Month/Year picker for payroll tabs */}
          {(activeTab === 3 || activeTab === 4) && (
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                label="Year"
                type="number"
                size="small"
                value={payrollYear}
                onChange={(e) => setPayrollYear(Number(e.target.value))}
                inputProps={{ min: 2020, max: 2100 }}
                sx={{ width: 100 }}
              />
              <TextField
                label="Month"
                type="number"
                size="small"
                value={payrollMonth}
                onChange={(e) => setPayrollMonth(Number(e.target.value))}
                inputProps={{ min: 1, max: 12 }}
                sx={{ width: 80 }}
              />
              <Typography variant="body2" color="text.secondary">
                Filtering by: <strong>{payrollYear}-{String(payrollMonth).padStart(2, "0")}</strong>
              </Typography>
            </Box>
          )}

          <Box>
            {activeTab === 0 && (
              <BookingCostsTab
                costs={bookingCosts}
                apartmentId={apartmentId}
                onUpdate={fetchCosts}
              />
            )}
            {activeTab === 1 && (
              <OperationsCostsTab
                costs={operationsCosts}
                apartmentId={apartmentId}
                onUpdate={fetchCosts}
              />
            )}
            {activeTab === 2 && <StaffTab />}
            {activeTab === 3 && (
              <CleaningSessionManager year={payrollYear} month={payrollMonth} />
            )}
            {activeTab === 4 && (
              <PayStubGenerator year={payrollYear} month={payrollMonth} />
            )}
            {activeTab === 5 && <SalariesTab />}
            {activeTab === 6 && <CostsReportTab />}
          </Box>
        </Paper>
      </div>
            <Footer />
    </div>
  );
};

export default OwnerCosts;
